import { PDFDocument, PDFName } from "pdf-lib";

// Utility function to convert base64 to Uint8Array
const base64ToUint8Array = (base64) => {
  try {
    // Accept both raw base64 and data URLs
    let b64 = base64 || "";
    if (b64.includes(",")) {
      b64 = b64.split(",")[1];
    }
    // Normalize URL-safe base64 and strip whitespace
    b64 = b64.replace(/\s/g, "").replace(/-/g, "+").replace(/_/g, "/");

    const binaryString = atob(b64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  } catch (err) {
    console.error("base64ToUint8Array failed:", err);
    return new Uint8Array();
  }
};

// Ensure any input (Blob | Uint8Array | string base64) becomes Uint8Array
const ensureUint8Array = async (input) => {
  if (!input) return new Uint8Array();
  try {
    if (input instanceof Uint8Array) {
      return input;
    }
    if (typeof Blob !== "undefined" && input instanceof Blob) {
      const buf = await input.arrayBuffer();
      return new Uint8Array(buf);
    }
    if (typeof input === "string") {
      return base64ToUint8Array(input);
    }
    // ArrayBuffer
    if (input instanceof ArrayBuffer) {
      return new Uint8Array(input);
    }
  } catch (e) {
    console.error("ensureUint8Array failed:", e);
  }
  return new Uint8Array();
};

export const extractFormFields = async (pdfInput) => {
  try {
    // Use pdfjs for better annotation detection
    const bytes = await ensureUint8Array(pdfInput);

    // Import pdfjs dynamically
    const pdfjsLib = await import("pdfjs-dist/webpack");
    const pdfDoc = await pdfjsLib.getDocument({ data: bytes }).promise;

    const extractedFields = [];

    // Process each page for annotations
    for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum++) {
      const page = await pdfDoc.getPage(pageNum);

      try {
        const annotations = await page.getAnnotations();

        for (const annotation of annotations) {
          // Check if it's a widget annotation (form field)
          if (annotation.subtype === "Widget" && annotation.fieldType) {
            const rect = annotation.rect;
            if (!rect || rect.length < 4) continue;

            const [x1, y1, x2, y2] = rect;
            const field = {
              name:
                annotation.fieldName ||
                annotation.alternativeText ||
                `field_${pageNum}_${extractedFields.length}`,
              type:
                annotation.fieldType === "Tx"
                  ? "PDFTextField"
                  : annotation.fieldType === "Btn"
                    ? "PDFCheckBox"
                    : annotation.fieldType,
              value: annotation.fieldValue || annotation.buttonValue || "",
              x: Math.min(x1, x2),
              y: Math.min(y1, y2),
              width: Math.abs(x2 - x1),
              height: Math.abs(y2 - y1),
              page: pageNum,
              fontSize: Math.abs(y2 - y1) * 0.7,
              fontSizes: [],
              autoFontSize: true,
            };

            extractedFields.push(field);
          }
        }
      } catch (pageError) {
        // No annotations on page
      }
    }

    // If pdfjs didn't find any fields, try pdf-lib as fallback
    if (extractedFields.length === 0) {
      try {
        const pdfLibDoc = await PDFDocument.load(bytes);
        const form = pdfLibDoc.getForm();
        const fields = form.getFields();

        // Use pdf-lib extraction as before
        const pageRefsToIndices = new Map();
        pdfLibDoc.getPages().forEach((page, index) => {
          const pageRef = page.ref;
          pageRefsToIndices.set(
            `${pageRef.objectNumber} ${pageRef.generationNumber}`,
            index + 1
          );
        });

        const pdfLibFields = fields.map((field) => {
          const acroField = field.acroField;
          const name = field.getName() || "";
          const type = field.constructor.name;

          let value = "";
          if (type === "PDFTextField") {
            value = field.getText() || "";
          } else if (type === "PDFCheckBox") {
            const rawValue = acroField.dict.get(PDFName.of("V"));
            value = rawValue?.encodedName === "/Yes";
          }

          let rect = null;
          let pageIndex = 1;

          if (acroField?.dict) {
            const rectEntry = acroField.dict.get(PDFName.of("Rect"));
            const pageRef = acroField.dict.get(PDFName.of("P"));

            if (rectEntry?.array) {
              const [x1, y1, x2, y2] = rectEntry.array.map(
                (num) => num.numberValue || 0
              );
              rect = {
                x: Math.min(x1, x2),
                y: Math.min(y1, y2),
                width: Math.abs(x2 - x1),
                height: Math.abs(y2 - y1),
              };
            }

            if (pageRef) {
              const pageKey = `${pageRef.objectNumber} ${pageRef.generationNumber}`;
              pageIndex = pageRefsToIndices.get(pageKey) || 1;
            }
          }

          return {
            name,
            type,
            value,
            x: rect?.x || 0,
            y: rect?.y || 0,
            width: rect?.width || 0,
            height: rect?.height || 0,
            page: pageIndex,
            fontSize: rect?.height || 10,
            fontSizes: [],
            autoFontSize: true,
          };
        });

        extractedFields.push(...pdfLibFields);
      } catch (pdfLibError) {
        console.error("pdf-lib fallback also failed:", pdfLibError);
      }
    }

    return extractedFields;
  } catch (error) {
    console.error("extractFormFields failed:", error);
    return [];
  }
};
